/**
 * VSCode环境适配器 - 高性能版本
 * 提供VSCode环境下的安全DOM操作方法
 * 确保Vue组件在VSCode和普通浏览器环境中都能正常工作
 */

export const VSCodeAdapter = {
    // 性能优化：缓存检测结果
    _isVSCodeEnv: null,
    _vscodeUtils: null,
    _safeHTMLUtils: null,
    _initialized: false,
    _scrollThrottle: new Map(), // 滚动节流缓存

    /**
     * 初始化适配器（只执行一次）
     */
    init() {
        if (this._initialized) return;
        
        // 🔧 确保_scrollThrottle始终是Map对象
        if (!this._scrollThrottle || !(this._scrollThrottle instanceof Map)) {
            this._scrollThrottle = new Map();
        }
        
        this._isVSCodeEnv = typeof window !== 'undefined' && 
                           (window.vscodeVueChatUtils !== undefined || 
                            window.vscodeServices !== undefined);
        
        if (this._isVSCodeEnv) {
            this._vscodeUtils = window.vscodeVueChatUtils;
            this._safeHTMLUtils = window.safeHTML || this._vscodeUtils;
        }

        this._initialized = true;
    },

    /**
     * 检测是否在VSCode环境中（缓存版本）
     */
    isVSCodeEnvironment() {
        if (!this._initialized) this.init();
        return this._isVSCodeEnv;
    },

    /**
     * 获取VSCode安全工具（缓存版本）
     */
    getVSCodeUtils() {
        if (!this._initialized) this.init();
        return this._vscodeUtils;
    },

    /**
     * 获取安全HTML操作工具（缓存版本）
     */
    getSafeHTMLUtils() {
        if (!this._initialized) this.init();
        return this._safeHTMLUtils;
    },

    /**
     * 高性能的innerHTML设置
     * @param {HTMLElement} element - 目标元素
     * @param {string} html - HTML内容
     */
    setInnerHTML(element, html) {
        if (!element) return;
        if (typeof html !== 'string') html = String(html);

        // 快速内容比较（避免hash计算）
        const currentContent = element.dataset.lastContent;
        if (currentContent === html && html !== '') {
            return; // 内容未变化且不是清空操作，跳过更新
        }

        if (this.isVSCodeEnvironment()) {
            const vscodeUtils = this.getVSCodeUtils();
            
            // 优先使用VSCode提供的安全方法
            if (vscodeUtils && vscodeUtils.safeSetInnerHTML) {
                try {
                    vscodeUtils.safeSetInnerHTML(element, html);
                } catch (error) {
                    console.warn('[VSCodeAdapter] safeSetInnerHTML failed, falling back to innerHTML:', error);
                    element.innerHTML = html;
                }
            } else {
                // 如果没有VSCode安全方法，直接使用innerHTML（VSCode环境下应该是安全的）
                element.innerHTML = html;
            }
        } else {
            // 普通浏览器环境，直接使用innerHTML
            element.innerHTML = html;
        }

        // 缓存内容用于下次比较
        element.dataset.lastContent = html;
    },

    /**
     * 高性能的滚动操作（带节流）
     * @param {HTMLElement} element - 滚动容器元素
     */
    scrollToBottom(element) {
        if (!element) return;

        // 🔧 确保_scrollThrottle存在
        if (!this._scrollThrottle || !(this._scrollThrottle instanceof Map)) {
            this._scrollThrottle = new Map();
        }

        const elementId = element.id || 'default';
        const now = Date.now();
        const lastScroll = this._scrollThrottle.get(elementId);
        
        // 节流：100ms内只执行一次滚动
        if (lastScroll && now - lastScroll < 100) {
            return;
        }
        
        this._scrollThrottle.set(elementId, now);

        if (this.isVSCodeEnvironment()) {
            const vscodeUtils = this.getVSCodeUtils();
            
            // 优先使用VSCode提供的安全滚动方法
            if (vscodeUtils && vscodeUtils.safeScrollToBottom) {
                try {
                    vscodeUtils.safeScrollToBottom(element);
                } catch (error) {
                    console.warn('[VSCodeAdapter] safeScrollToBottom failed, falling back:', error);
                    this.fallbackScrollToBottom(element);
                }
            } else {
                this.fallbackScrollToBottom(element);
            }
        } else {
            // 高性能滚动
            this.performantScrollToBottom(element);
        }
    },

    /**
     * 高性能的滚动到底部实现
     * @param {HTMLElement} element - 滚动容器
     */
    performantScrollToBottom(element) {
        // 使用 RAF 确保DOM更新完成，但避免多次调用
        const rafId = element.dataset.rafId;
        if (rafId) {
            cancelAnimationFrame(rafId);
        }

        const newRafId = requestAnimationFrame(() => {
            try {
                element.scrollTop = element.scrollHeight;
                delete element.dataset.rafId;
            } catch (error) {
                // 静默处理错误，避免日志污染
            }
        });

        element.dataset.rafId = newRafId;
    },

    /**
     * 降级的滚动到底部方法（优化版本）
     * @param {HTMLElement} element - 滚动容器
     */
    fallbackScrollToBottom(element) {
        try {
            if (typeof requestAnimationFrame === 'function') {
                this.performantScrollToBottom(element);
            } else {
                element.scrollTop = element.scrollHeight;
            }
        } catch (error) {
            // 静默处理错误
        }
    },

    /**
     * 批量设置innerHTML（高性能批处理）
     * @param {Array} updates - 更新数组 [{element, html}, ...]
     */
    batchSetInnerHTML(updates) {
        if (!Array.isArray(updates) || updates.length === 0) return;

        // 批量处理，减少DOM重排
        const fragment = document.createDocumentFragment();
        
        updates.forEach(({element, html}) => {
            if (element && typeof html === 'string') {
                this.setInnerHTML(element, html);
            }
        });
    },

    /**
     * 清理适配器缓存（用于内存管理）
     */
    cleanup() {
        // 🔧 安全地清理滚动节流缓存
        if (this._scrollThrottle && typeof this._scrollThrottle.clear === 'function') {
            this._scrollThrottle.clear();
        } else {
            // 如果_scrollThrottle不存在或不是Map，重新初始化
            this._scrollThrottle = new Map();
        }
        
        // 清理所有元素的RAF ID
        document.querySelectorAll('[data-raf-id]').forEach(el => {
            if (el.dataset.rafId) {
                cancelAnimationFrame(el.dataset.rafId);
                delete el.dataset.rafId;
            }
        });
    },

    /**
     * 安全的事件监听（简化版本）
     */
    addEventListener(element, event, handler) {
        if (!element || typeof handler !== 'function') return;
        
        try {
            element.addEventListener(event, handler);
        } catch (error) {
            // 静默处理错误
        }
    },

    /**
     * 获取环境信息（调试用）
     */
    getEnvironmentInfo() {
        if (!this._initialized) this.init();
        
        return {
            isVSCode: this._isVSCodeEnv,
            hasVSCodeUtils: !!this._vscodeUtils,
            hasSafeHTML: !!this._safeHTMLUtils,
            scrollThrottleSize: this._scrollThrottle.size
        };
    },

    /**
     * 性能监控（仅在开发模式下启用）
     */
    logPerformanceInfo() {
        if (process.env.NODE_ENV === 'development') {
            console.debug('[VSCodeAdapter] Performance Info:', this.getEnvironmentInfo());
        }
    }
};

// 默认导出
export default VSCodeAdapter; 
